import React, { useEffect, useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  AggregateItem,
  ASCENDING_DIRECTION,
  normalizeSortDirection,
  SORT_DIRECTIONS,
  SortDirection,
} from 'constants/reports-templates';
import { ReportingColumn } from 'interfaces/report-configuration.interface';
import { OptionType } from 'interfaces/shared.interface';
import { SelectFormInput } from 'shared-components';
import ModalControlButtons from 'shared-components/ModalControlButtons';

type Props = {
  control: any;
  selectedColumnsData: ReportingColumn[];
  onCloseModal: VoidFunction;
  onSubmit: (column: string, direction: SortDirection) => void;
  savedAggregates: AggregateItem[];
  initialSorting?: { column: string; direction: SortDirection };
  selectedSortingColumn?: string;
  selectedSortingColumnName?: string;
};

const CreateReportTemplateSortByModal: React.FC<Props> = ({
  control,
  selectedColumnsData,
  onCloseModal,
  onSubmit,
  savedAggregates = [],
  initialSorting,
}) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<string>(initialSorting?.column ?? '');
  const [direction, setDirection] = useState<SortDirection | ''>(initialSorting?.direction ?? '');
  const { setValue } = useFormContext();

  const availableColumns: OptionType[] = useMemo(() => {
    const base =
      savedAggregates.length > 0
        ? savedAggregates.map((aggregate) => {
            const label = aggregate.column ? `${aggregate.func} ${aggregate.column}` : aggregate.func;
            return { label: label || '', value: label || '' };
          })
        : selectedColumnsData.map((col) => ({ label: col.name, value: col.name }));

    return base;
  }, [savedAggregates, selectedColumnsData]);

  useEffect(() => {
    if (initialSorting?.column) {
      setValue('sorting', {
        label: initialSorting.column,
        value: initialSorting.column,
      });
      setSorting(initialSorting.column);
    }

    if (initialSorting?.direction) {
      const normalizedDirection = normalizeSortDirection(initialSorting.direction);

      const directionLabel = normalizedDirection === ASCENDING_DIRECTION ? 'Ascending' : 'Descending';

      setValue('sortingDirection', {
        label: directionLabel,
        value: normalizedDirection,
      });

      setDirection(normalizedDirection);
    }
  }, [initialSorting, setValue]);

  return (
    <div className="pt-4 mb-2 w-[40rem]">
      <div className="flex items-center w-full gap-2">
        <SelectFormInput
          name="sorting"
          control={control}
          options={availableColumns}
          placeholder={t('SelectColumn')}
          onSelectValue={(option) => setSorting(option?.value ?? '')}
          defaultValue={availableColumns.find((o) => o.value === initialSorting?.column)}
        />
        {sorting && (
          <SelectFormInput
            name="sortingDirection"
            control={control}
            options={SORT_DIRECTIONS}
            placeholder={t('SelectDirection')}
            onSelectValue={(option) => setDirection(option?.value as SortDirection)}
            defaultValue={SORT_DIRECTIONS.find((o) => o.value === initialSorting?.direction)}
          />
        )}
      </div>
      <ModalControlButtons
        onClose={onCloseModal}
        saveButtonText={t('Save')}
        disabled={!sorting || !direction}
        onSubmit={() => onSubmit(sorting, direction as SortDirection)}
      />
    </div>
  );
};

export default CreateReportTemplateSortByModal;
